import React from "react";
import { Card, CardBody, Spinner, Badge, Button } from "reactstrap";
import { useGetPurchaseOrderAuditLogsQuery } from "slices/InventoryManagement/inventoryManagementSlice";
import { formatDateTime } from "helpers/date_utils";

interface AuditLogTabProps {
  purchaseOrderId?: number | string;
}

const actionIcon = (action: string): string => {
  switch (action) {
    case "quantity_updated":
      return "ri-edit-line";
    case "status_change":
      return "ri-refresh-line";
    case "delivery_received":
      return "ri-check-double-line";
    case "defect_reported":
      return "ri-error-warning-line";
    case "re_delivery_initiated":
      return "ri-truck-line";
    case "split_created":
    case "split_updated":
      return "ri-dashboard-horizontal-line";
    default:
      return "ri-history-line";
  }
};

const actionColor = (action: string): string => {
  switch (action) {
    case "quantity_updated":
      return "primary";
    case "status_change":
      return "info";
    case "delivery_received":
      return "success";
    case "defect_reported":
      return "danger";
    case "re_delivery_initiated":
      return "warning";
    case "split_created":
    case "split_updated":
      return "secondary";
    default:
      return "secondary";
  }
};

const AuditLogTab = ({ purchaseOrderId }: AuditLogTabProps) => {
  const { data, isLoading, isError, refetch } =
    useGetPurchaseOrderAuditLogsQuery(purchaseOrderId as any, {
      skip: !purchaseOrderId,
    });

  const logs: any[] = data?.results || [];

  const downloadPdf = (url?: string | null) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => document.body.removeChild(link), 100);
  };

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Audit Log</h5>
          <Button color="light" size="sm" onClick={() => refetch()}>
            <i className="ri-refresh-line align-middle me-1"></i>
            Refresh
          </Button>
        </div>
        <hr />

        {isLoading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        )}

        {isError && !isLoading && (
          <div className="alert alert-danger">
            Unable to load the audit log. Please try again.
          </div>
        )}

        {!isLoading && !isError && logs.length === 0 && (
          <div className="text-center py-5">
            <i className="ri-history-line display-4 text-muted opacity-50"></i>
            <h5 className="mt-3 text-muted">No Audit History</h5>
            <p className="text-muted mb-0">
              Changes to this purchase order will appear here.
            </p>
          </div>
        )}

        {!isLoading && !isError && logs.length > 0 && (
          <div>
            {logs.map((log: any, idx: number) => {
              const beforeVersion = (log.pdf_versions || []).find(
                (v: any) => v.label === "before"
              );
              const afterVersion = (log.pdf_versions || []).find(
                (v: any) => v.label === "after"
              );
              const color = actionColor(log.action);
              const isLast = idx === logs.length - 1;
              return (
                <div key={log.id} className="d-flex">
                  {/* Timeline rail: dot + connector */}
                  <div className="d-flex flex-column align-items-center me-3">
                    <span
                      className={`rounded-circle bg-${color}-subtle text-${color} d-flex align-items-center justify-content-center flex-shrink-0`}
                      style={{ width: 32, height: 32 }}
                    >
                      <i className={actionIcon(log.action)}></i>
                    </span>
                    {!isLast && (
                      <span
                        className="flex-grow-1 my-1"
                        style={{
                          width: 2,
                          backgroundColor: "var(--vz-border-color, #e9ebec)",
                        }}
                      ></span>
                    )}
                  </div>

                  {/* Entry card */}
                  <div className={`border rounded p-3 flex-grow-1 ${isLast ? "" : "mb-3"}`}>
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <Badge color={actionColor(log.action)} className="me-2">
                        {log.action_display || log.action}
                      </Badge>
                      <span className="text-muted fs-12">
                        {log.created_at
                          ? formatDateTime(log.created_at, "datetime")
                          : ""}
                      </span>
                    </div>
                    <div className="text-muted fs-12">
                      {log.performed_by_name || "System"}
                    </div>
                  </div>

                  <div className="mt-2">{log.description}</div>

                  {(log.old_quantity != null || log.new_quantity != null) && (
                    <div className="mt-1 small">
                      <span className="text-muted">Quantity: </span>
                      <span className="fw-semibold">
                        {log.old_quantity ?? "—"} → {log.new_quantity ?? "—"}
                      </span>
                    </div>
                  )}

                  {(beforeVersion || afterVersion) && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {beforeVersion && (
                        <Button
                          size="sm"
                          color="outline-secondary"
                          onClick={() => downloadPdf(beforeVersion.signed_url)}
                          disabled={!beforeVersion.signed_url}
                        >
                          <i className="ri-file-download-line align-middle me-1"></i>
                          Before PDF
                        </Button>
                      )}
                      {afterVersion && (
                        <Button
                          size="sm"
                          color="outline-primary"
                          onClick={() => downloadPdf(afterVersion.signed_url)}
                          disabled={!afterVersion.signed_url}
                        >
                          <i className="ri-file-download-line align-middle me-1"></i>
                          After PDF
                        </Button>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default AuditLogTab;
